// src/stats/KpiCard.tsx
import { Sparkline } from './Sparkline'
import { formatMetricValue, formatDelta, isDeltaPositive } from './format'

interface KpiCardProps {
  metric: string
  label: string
  current: number
  previous: number | null
  /** Points de série pour le mini-graphe (optionnel) */
  seriesPoints?: { date: string; value: number }[]
  /** Badge affiché si la source n'est pas configurée */
  notConfigured?: boolean
  testId?: string
}

export function KpiCard({
  metric,
  label,
  current,
  previous,
  seriesPoints,
  notConfigured = false,
  testId,
}: KpiCardProps) {
  const deltaStr = formatDelta(current, previous)
  const positive = previous !== null && previous !== 0
    ? isDeltaPositive(metric, current, previous)
    : null

  const deltaColor =
    positive === null
      ? 'var(--dim)'
      : positive
        ? '#4caf7a'
        : 'var(--arancio)'

  return (
    <div
      data-testid={testId ?? `kpi-${metric.replace(/_/g, '-')}`}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1.125rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
        position: 'relative',
        minWidth: 0,
      }}
    >
      {/* Badge "non configuré" */}
      {notConfigured && (
        <span
          style={{
            position: 'absolute',
            top: '0.625rem',
            right: '0.625rem',
            fontSize: '0.625rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: 'var(--dim)',
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '4px',
            padding: '0.125rem 0.375rem',
          }}
        >
          non configuré
        </span>
      )}

      {/* Label */}
      <span
        style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--muted)',
        }}
      >
        {label}
      </span>

      {/* Valeur + Sparkline */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.5rem' }}>
        <span
          style={{
            fontSize: '1.625rem',
            fontWeight: 700,
            color: notConfigured ? 'var(--dim)' : 'var(--fg)',
            fontFamily: 'var(--mono)',
            lineHeight: 1,
          }}
        >
          {notConfigured ? '—' : formatMetricValue(metric, current)}
        </span>

        {seriesPoints && seriesPoints.length >= 2 && !notConfigured && (
          <Sparkline points={seriesPoints} width={72} height={28} />
        )}
      </div>

      {/* Delta vs période précédente */}
      {deltaStr !== null && !notConfigured && (
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: deltaColor,
          }}
        >
          {deltaStr} vs période précédente
        </span>
      )}
    </div>
  )
}
